<script>
import {
  notificationComputed,
  notificationMethods,
} from '@state/helpers/notification'
import formatDate from '@utils/format-date'

import { DATE_TIME_FORMAT_SPACING } from '@utils/constant'
import { isEmpty, flatMap } from 'lodash'

export default {
  props: {
    type: {
      validator: function(value) {
        return !!~['add', 'view', 'edit'].indexOf(value)
      },
      default: 'add',
    },
    notificationDetail: {
      type: Object,
      default: () => {},
    },
    failed: {
      type: Boolean,
      default: false,
    },
    triggerMask: {
      type: Boolean,
      require: false,
    },
  },
  data() {
    return {
      watchData: false,
      toggle: false,
      triggerPreview: false,
      data: {},
    }
  },
  computed: {
    ...notificationComputed,
    add() {
      return this.type === 'add'
    },
    edit() {
      return this.type === 'edit'
    },
    view() {
      return this.type === 'view'
    },
    scheduleDate() {
      const startDate = formatDate(
        this.data.schedule_start,
        DATE_TIME_FORMAT_SPACING
      )
      const endDate = formatDate(
        this.data.schedule_end,
        DATE_TIME_FORMAT_SPACING
      )
      return `${startDate || ''}    ～    ${endDate || ''}`
    },
    memberCount() {
      const previewCount = this.getPreviewCount
      return previewCount ? `${previewCount.member_count}人` : ''
    },
  },
  watch: {
    notificationDetail() {
      if (this.watchData) {
        const data = this.notificationDetail
        this.data = { ...data }
        this.handlePreview()
      }
    },
    type() {
      if (this.type === 'edit') {
        this.watchData = false
      }
    },
  },
  created() {
    if (this.view) {
      this.toggle = true
      this.triggerPreview = true
      this.watchData = true
    } else {
      this.watchData = false
    }
  },
  methods: {
    ...notificationMethods,
    expandPreview() {
      this.toggle = !this.toggle
    },
    async handlePreview() {
      const data = this.notificationDetail
      const errors = {}
      if (!data.content || data.content.trim().length === 0) {
        errors.content = [this.$t('validation.required')]
      }
      if (!data.title || data.title.trim().length === 0) {
        errors.title = [this.$t('validation.required')]
      }

      if (!isEmpty(errors)) {
        this.$emit('validate', errors)
        this.$emit('preview-update', false)
        return
      }
      if (this.failed) {
        this.$emit('preview-update', false)
        return
      }

      this.data = { ...data }
      this.triggerPreview = true
      this.toggle = true // toggle expand
      if (!data.filter_obj) {
        await this.fetchingPreviewCount({ filter: 0 })
      } else {
        const filterObj = data.filter_obj
        await this.fetchingPreviewCount({
          filter: 1,
          gender: parseInt(filterObj.gender, 10),
          age_range: this.formatRangerNumber(filterObj, 'age_range'),
          last_measure_date: this.formatRangerDate(
            filterObj,
            'last_measure_date'
          ),
          weight_range: this.formatArrayNumber(filterObj?.weight_range),
          body_fat_rate_range: this.formatArrayNumber(
            filterObj?.body_fat_rate_range
          ),
          body_water_rate_range: this.formatArrayNumber(
            filterObj?.body_water_rate_range
          ),
          BMR_range: this.formatArrayNumber(filterObj?.BMR_range),
          metabolic_age_range: this.formatArrayNumber(
            filterObj?.metabolic_age_range
          ),
          muscle_rate_range: this.formatArrayNumber(
            filterObj?.muscle_rate_range
          ),
          bone_mass_range: this.formatArrayNumber(filterObj?.bone_mass_range),
          subcutaneous_fat_range: this.formatArrayNumber(
            filterObj?.subcutaneous_fat_range
          ),
          visceral_fat_range: this.formatArrayNumber(
            filterObj?.visceral_fat_range
          ),
          BMI_range: this.formatArrayNumber(filterObj?.BMI_range),
          muscle_mass_range: this.formatArrayNumber(
            filterObj?.muscle_mass_range
          ),
          protein_range: this.formatArrayNumber(filterObj?.protein_range),
          compare_weight: this.formatRangerNumber(filterObj, 'weight_differ'),
          compare_BodyFatRate: this.formatRangerNumber(
            filterObj,
            'body_fat_rate_differ'
          ),
          admin_note: filterObj.admin_note,
          answer1: filterObj.answer1 ? filterObj.answer1.split(',') : [],
          answer2: filterObj.answer2 ? filterObj.answer2.split(',') : [],
          answer3: filterObj.answer3 ? filterObj.answer3.split(',') : [],
          answer4: filterObj.answer4 ? filterObj.answer4.split(',') : [],
          status: filterObj.member_status
            ? filterObj.member_status.split(',').map(Number)
            : [],
        })
      }
      this.$emit('preview-update', true)
    },
    handlePreviewImage() {
      if (this.data.image_binary) {
        return this.data.image_binary
      } else if (this.data.image_url) {
        const { origin } = window.location
        return `${origin}/${this.data.image_url}`
      }
      return ''
    },
    formatArrayNumber(data) {
      if (!data) {
        return []
      }
      return flatMap(
        data.split(',').map((it) => {
          if (it && parseInt(it, 10)) {
            return parseInt(it, 10)
          }
          return []
        }) || []
      )
    },
    formatRangerNumber(data, name) {
      let lower = -9999
      let upper = -9999
      if (
        data[`${name}_lower`] ||
        data[`${name}_lower`] === '0' ||
        data[`${name}_lower`] === 0
      ) {
        lower = parseInt(data[`${name}_lower`], 10)
      }
      if (
        data[`${name}_upper`] ||
        data[`${name}_upper`] === '0' ||
        data[`${name}_upper`] === 0
      ) {
        upper = parseInt(data[`${name}_upper`], 10)
      }
      return [lower, upper]
    },
    formatRangerDate(data, name) {
      let lower = ''
      let upper = ''
      if (data[`${name}_lower`]) {
        lower = data[`${name}_lower`]
      }
      if (data[`${name}_upper`]) {
        upper = data[`${name}_upper`]
      }
      return [lower, upper]
    },
  },
}
</script>

<template>
  <div id="notification_agentadmin-preview">
    <BaseCardDetail title="プレビュー" form-collapsed :trigger-mask="edit">
      <template v-slot:header-action>
        <CButton
          variant="outline"
          color="dark"
          class="py-0"
          @click="handlePreview"
        >
          表示更新
        </CButton>
      </template>
      <div
        class="d-flex justify-content-between toggle-nav border border-bottom-0"
      >
        <div>
          <div v-show="data && data.tag" class="preview_tag">
            {{ data.tag }}
          </div>
        </div>
        <div class="float-right mr-4 preview_company">
          <div>
            {{ data.company }}
          </div>
        </div>
        <CButton
          :class="[toggle ? 'active' : 'not-active', 'toggle-nav-btn']"
          @click="expandPreview"
        >
          <font-awesome-icon icon="chevron-down" />
        </CButton>
      </div>
      <div class="p-3 pt-4 border mb-3">
        <div v-if="triggerPreview">
          <div v-show="toggle" class="preview-image-wrapper">
            <CImg
              v-if="handlePreviewImage()"
              class="preview-image border"
              :src="handlePreviewImage()"
            />
          </div>
          <h4 class="text-break font-weight-bold mt-2">
            {{ data.title }}
          </h4>
          <div v-show="toggle" class="text-break" style="white-space: pre-line">
            {{ data.content }}
          </div>
        </div>
      </div>
      <div class="border-top preview-footer">
        <CRow>
          <CCol sm="3">
            期間
          </CCol>
          <CCol sm="9">
            <BaseInput plaintext :value="triggerPreview ? scheduleDate : ''" />
          </CCol>
          <CCol sm="3">
            対象 (参考)
          </CCol>
          <CCol sm="9">
            <BaseInput
              plaintext
              :value="`${triggerPreview ? memberCount : ''}`"
            />
          </CCol>
        </CRow>
      </div>
    </BaseCardDetail>
  </div>
</template>

<style lang="scss" scoped>
#notification_agentadmin-preview {
  ::v-deep .preview_tag {
    padding: 0 15px;
    color: black;
    background-color: white;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
  }

  ::v-deep .preview_company {
    margin-right: 20px;
    font-size: 70%;
    color: white;
  }

  ::v-deep .toggle-nav-btn {
    position: absolute;
    top: 30px;
    right: 0;
    width: 40px;
    height: 40px;
    color: white;
    background-color: $toggle-nav-color;
    border: none;
    border-radius: 25px;
    outline: none;

    &:focus {
      box-shadow: none;
    }
  }

  ::v-deep .toggle-nav {
    position: relative;
    height: 50px;
    padding: 15px 0;
    background-color: $toggle-nav-color;
  }

  ::v-deep .active {
    > svg {
      transition: transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms;
      transform: rotate(-180deg);
    }
  }

  ::v-deep .not-active {
    > svg {
      transition: transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms;
      transform: rotate(0deg);
    }
  }

  .preview-image {
    max-width: 100%;
    max-height: 100%;
  }

  .preview-image-wrapper {
    min-height: 20px;
  }

  .preview-footer {
    padding: 1.25rem;
    margin-right: -1.25rem;
    margin-left: -1.25rem;
  }
}
</style>
